import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Edit, Trash2, BookOpen, Upload } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicationsManagement = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title_bengali: '',
    title_english: '',
    description_bengali: '',
    description_english: '',
    author: '',
    price: '',
    cover_image_url: '',
    pdf_url: '',
    published_year: new Date().getFullYear(),
  });

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/publications`, getAuthHeaders());
      setPublications(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch publications', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('category', 'publications');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/image`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setFormData({ ...formData, cover_image_url: response.data.url });
      toast({ title: 'Success', description: 'Cover image uploaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Image upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handlePDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('category', 'publications');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/pdf`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setFormData({ ...formData, pdf_url: response.data.url });
      toast({ title: 'Success', description: 'PDF uploaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'PDF upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingPublication) {
        await axios.put(
          `${API_URL}/api/admin/publications/${editingPublication.id}`,
          formData,
          getAuthHeaders()
        );
        toast({ title: 'Success', description: 'Publication updated successfully' });
      } else {
        await axios.post(`${API_URL}/api/admin/publications`, formData, getAuthHeaders());
        toast({ title: 'Success', description: 'Publication created successfully' });
      }
      
      resetForm();
      fetchPublications();
      setShowModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save publication', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this publication?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/publications/${id}`, getAuthHeaders());
      toast({ title: 'Success', description: 'Publication deleted' });
      fetchPublications();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete publication', variant: 'destructive' });
    }
  };

  const handleEdit = (publication) => {
    setEditingPublication(publication);
    setFormData({
      title_bengali: publication.title_bengali || '',
      title_english: publication.title_english || '',
      description_bengali: publication.description_bengali || '',
      description_english: publication.description_english || '',
      author: publication.author || '',
      price: publication.price || '',
      cover_image_url: publication.cover_image_url || '',
      pdf_url: publication.pdf_url || '',
      published_year: publication.published_year || new Date().getFullYear(),
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title_bengali: '',
      title_english: '',
      description_bengali: '',
      description_english: '',
      author: '',
      price: '',
      cover_image_url: '',
      pdf_url: '',
      published_year: new Date().getFullYear(),
    });
    setEditingPublication(null);
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/300x400?text=No+Cover';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads') || url.startsWith('/uploads')) return `${API_URL}${url}`;
    return url;
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Tripura Darpan Publications</CardTitle>
            <CardDescription>Manage books and publications</CardDescription>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Publication
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {publications.map((pub) => (
                <Card key={pub.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative h-64 bg-gray-100">
                    <img
                      src={getImageUrl(pub.cover_image_url)}
                      alt={pub.title_bengali}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/300x400?text=No+Cover'; }}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg line-clamp-2 mb-1">{pub.title_bengali}</h3>
                    {pub.title_english && (
                      <p className="text-sm text-gray-600 line-clamp-1 mb-2">{pub.title_english}</p>
                    )}
                    <p className="text-xs text-gray-500 mb-2">by {pub.author}</p>
                    <div className="flex justify-between items-center mb-3">
                      <Badge variant="outline">₹{pub.price}</Badge>
                      <span className="text-xs text-gray-500">{pub.published_year}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(pub)}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(pub.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {!loading && publications.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p>No publications yet. Add your first book!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPublication ? 'Edit Publication' : 'Add New Publication'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title (Bengali) *</Label>
                <Input
                  value={formData.title_bengali}
                  onChange={(e) => setFormData({ ...formData, title_bengali: e.target.value })}
                  placeholder="বইয়ের শিরোনাম"
                  required
                />
              </div>
              <div>
                <Label>Title (English)</Label>
                <Input
                  value={formData.title_english}
                  onChange={(e) => setFormData({ ...formData, title_english: e.target.value })}
                  placeholder="Book title"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Author *</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="লেখকের নাম"
                  required
                />
              </div>
              <div>
                <Label>Price (₹) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="299"
                  required
                />
              </div>
              <div>
                <Label>Published Year *</Label>
                <Input
                  type="number"
                  value={formData.published_year}
                  onChange={(e) => setFormData({ ...formData, published_year: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Description (Bengali) *</Label>
              <Textarea
                value={formData.description_bengali}
                onChange={(e) => setFormData({ ...formData, description_bengali: e.target.value })}
                rows={4}
                placeholder="বইয়ের সংক্ষিপ্ত বিবরণ..."
                required
              />
            </div>

            <div>
              <Label>Description (English)</Label>
              <Textarea
                value={formData.description_english}
                onChange={(e) => setFormData({ ...formData, description_english: e.target.value })}
                rows={4}
                placeholder="Brief description..."
              />
            </div>

            <div>
              <Label>Cover Image *</Label>
              <Input type="file" accept="image/*" onChange={handleImageUpload} />
              {uploading && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
              {formData.cover_image_url && (
                <img
                  src={getImageUrl(formData.cover_image_url)}
                  alt="Cover preview"
                  className="mt-2 h-40 rounded border"
                />
              )}
            </div>

            <div>
              <Label>PDF File (Optional - for digital version)</Label>
              <Input type="file" accept="application/pdf" onChange={handlePDFUpload} />
              {formData.pdf_url && (
                <p className="text-sm text-green-600 mt-1">✓ PDF uploaded</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || uploading} className="bg-red-600 hover:bg-red-700">
                {loading ? 'Saving...' : editingPublication ? 'Update' : 'Create'} Publication
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicationsManagement;
